import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Select, { SingleValue } from "react-select";
import { Row, Col, Button, Spinner } from "reactstrap";
import { Drawer } from "antd";
import rolesApi from "../../../api/master/roles";

interface Employee {
  name: string;
  id: string;
}

interface Role {
  roleName: string;
  employees: Employee[];
  roleID: any;
  level: string;
}

interface CustomDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ApprovalData[]) => void;
  setApprovalSubmissionStatus: (status: boolean) => void;
  approvalData: ApprovalData[];
  siteId: number;
  moduleId?: number;
  title?: string;
  onError?: (message: string) => void;
}

interface FormValues {
  [key: string]: any;
}

interface ApprovalData {
  roleId: any;
  level: string;
  userId: any;
  approvalStage?: string;
}


const QuestionerApprovalDrawer: React.FC<CustomDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  setApprovalSubmissionStatus,
  approvalData,
  siteId,
  moduleId = 211,
  title = "Set Approvals",
  onError,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchApprovalHierarchy = async () => {
    try {
      const res = await rolesApi.fetchHierarchyUsersByModuleId(
        moduleId,
        siteId
      );
      if (!res) return;
      const { data, status, message } = res;
      if (status && data?.length > 0) {
        setRoles(data);
      } else {
        setRoles([]);
        if (!status && message && onError) {
          onError(message);
        }
      }
    } catch (error) {
      console.error("Failed to fetch approval hierarchy:", error);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovalHierarchy();
  }, [moduleId, siteId]);

  useEffect(() => {
    roles.forEach((role, index) => {
      register(role.level, { required: "This field is required" }); // uncomment this line if you want to show the required field error
    });
  }, [roles, register]);

  useEffect(() => {
    if (approvalData && approvalData.length > 0) {
      approvalData.forEach((data) => {
        setValue(data.level, data.userId);
        if (data.approvalStage) {
          setValue(`approvalStage_${data.level}`, {
            value: data.approvalStage,
            label: data.approvalStage,
          });
        }
      });
      setLoading(false);
    }
  }, [approvalData, setValue]);

  const submitApprovals: SubmitHandler<FormValues> = (data: any) => {
    const approvalData: ApprovalData[] = roles.map((role, index) => {
      const levelKey = `level${role.level}`;
      const approvalStageKey = `approvalStage_${role.level}`;

      return {
        roleId: Number(role.roleID),
        level: role.level,
        userId: data[`${role.level}`],
        approvalStage: data[approvalStageKey]?.value,
      };
    });

    onSubmit(approvalData);
    setApprovalSubmissionStatus(true);
  };

  const handleSelectChange = (
    name: string,
    value: SingleValue<{ value: string; label: string }>
  ) => {
    if (value && value.value) {
      setValue(name, value.value, { shouldValidate: true });
    }
  };

  const handleStageSelectChange = (fieldName: string, selectedOption: any) => {
    setValue(fieldName, selectedOption); // assuming you're using react-hook-form's setValue
  };

  const getDefaultValue = (role: Role) => {
    const approval = approvalData?.find((data) => data.level === role.level);
    if (approval) {
      const employee = role.employees?.find(
        (emp) => emp.id === approval.userId
      );
      if (employee) {
        return { value: employee.id, label: employee.name };
      }
    }
    return null;
  };

  const getDefaultStageValue = (role: Role) => {
    const approval = approvalData?.find((data) => data.level === role.level);
    if (approval?.approvalStage) {
      return { value: approval.approvalStage, label: approval.approvalStage };
    }
    return null;
  };

  return (
    <Drawer
      title={title}
      placement="right"
      onClose={onClose}
      open={open}
      width={"500px"}
    >
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Spinner color="primary" />
        </div>
      ) : (
        <div>
          <p>
            There are {roles.length} levels of approvals set for this process
          </p>
          <form onSubmit={handleSubmit(submitApprovals)}>
            {roles.map((role, index) => (
              <Row className="mt-2" key={index}>
                <Col lg="6">
                  <label>{`${role.roleName}`}</label>
                  <Select
                    classNamePrefix="react-select"
                    options={role.employees?.map((employee) => ({
                      value: employee.id,
                      label: employee.name,
                    }))}
                    onChange={(selectedOption) =>
                      handleSelectChange(`${role.level}`, selectedOption)
                    }
                    placeholder={`Select ${role.roleName}`}
                    defaultValue={getDefaultValue(role)}
                  />
                  {errors[`${role.level}`] && (
                    <p className="mandatory">
                      {errors[`${role.level}`]?.message as string}
                    </p>
                  )}
                </Col>
                <Col lg="6">
                  <label>{`Approval Stage`}</label>
                  <Select
                    classNamePrefix="react-select"
                    options={[
                      { value: "Checker", label: "Checker" },
                      { value: "Approver", label: "Approver" },
                      { value: "Verifier", label: "Verifier" },
                    ]}
                    onChange={(selectedOption) =>
                      handleStageSelectChange(
                        `approvalStage_${role.level}`,
                        selectedOption
                      )
                    }
                    placeholder={`Select Approval Stage`}
                    defaultValue={getDefaultStageValue(role)}
                  />
                  {errors[`${role.level}`] && (
                    <p className="mandatory">
                      {errors[`${role.level}`]?.message as string}
                    </p>
                  )}
                </Col>
              </Row>
            ))}
            <Button type="submit" color="primary" className="mt-3">
              Save
            </Button>
          </form>
        </div>
      )}
    </Drawer>
  );
};

export default React.memo(QuestionerApprovalDrawer);
